import React, { useMemo } from 'react';
import Typography from 'Components/Atoms/Typography';
import {
  CheckBox,
  CheckBoxOutlineBlank,
  ZoomIn,
  ZoomOut,
} from 'Components/Atoms/Icons';
import { ReservationShift } from 'types/shifts';
import { classHelper, blockToTime } from 'utils/helper';
import './styles.scss';

// Height (in pixels) of one row in the stacked timeline. Overlapping shifts
// cascade onto successive rows.
const ROW_HEIGHT = 26;
const ROW_GAP = 2;

export type ShiftTimelineProps = {
  shifts: ReservationShift[];
  selectedShiftIds: string[];
  onToggle: (shiftId: string) => void;
  onSelectAll: () => void;
  // Currently-visible block range from the main timetable viewport. When
  // provided (and it overlaps the timeline's range) a box outlined in the
  // secondary color is drawn over the track to show the operator where in
  // the day their horizontal scroll is.
  visibleStartBlock?: number | null;
  visibleCloseBlock?: number | null;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  canZoomIn?: boolean;
  canZoomOut?: boolean;
};

const ShiftTimeline: React.FC<ShiftTimelineProps> = ({
  shifts,
  selectedShiftIds,
  onToggle,
  onSelectAll,
  visibleStartBlock = null,
  visibleCloseBlock = null,
  onZoomIn,
  onZoomOut,
  canZoomIn = true,
  canZoomOut = true,
}) => {
  // Filter to shifts that can actually be drawn, then compute the day range
  // and pack overlapping shifts onto successive rows with a greedy
  // algorithm (sorted by start time, each shift lands on the lowest row
  // where it doesn't collide with any already placed there).
  const { rangeStart, rangeClose, laidOut, rowCount } = useMemo(() => {
    const usable = shifts.filter(
      (s) =>
        typeof s.start === 'number' &&
        typeof s.close === 'number' &&
        s.close > s.start,
    );

    if (!usable.length) {
      return {
        rangeStart: 0,
        rangeClose: 0,
        laidOut: [] as { shift: ReservationShift; row: number }[],
        rowCount: 0,
      };
    }

    const rangeStart = usable.reduce(
      (acc, cV) => Math.min(acc, cV.start),
      usable[0].start,
    );
    const rangeClose = usable.reduce(
      (acc, cV) => Math.max(acc, cV.close),
      usable[0].close,
    );

    const sorted = [...usable].sort((a, b) => a.start - b.start);
    const rows: ReservationShift[][] = [];
    const laidOut: { shift: ReservationShift; row: number }[] = [];

    for (const shift of sorted) {
      let row = rows.findIndex((bucket) =>
        bucket.every((s) => s.close <= shift.start || s.start >= shift.close),
      );
      if (row === -1) {
        row = rows.length;
        rows.push([]);
      }
      rows[row].push(shift);
      laidOut.push({ shift, row });
    }

    return {
      rangeStart,
      rangeClose,
      laidOut,
      rowCount: Math.max(rows.length, 1),
    };
  }, [shifts]);

  if (!laidOut.length) return null;

  const totalBlocks = rangeClose - rangeStart;
  const allSelected = laidOut.every(({ shift }) =>
    selectedShiftIds.includes(shift.id as string),
  );

  const trackHeight = rowCount * ROW_HEIGHT + (rowCount - 1) * ROW_GAP;

  // Hourly gridlines: one vertical line every 4 blocks (= 1 h). We compute
  // the line spacing as a percentage of the track and the offset to the
  // first full-hour boundary inside the range, then expose both as CSS
  // custom properties that the SCSS uses to drive a repeating
  // linear-gradient background.
  const hourWidthPct = totalBlocks > 0 ? (4 / totalBlocks) * 100 : 100;
  const firstHourBlock = Math.ceil(rangeStart / 4) * 4;
  const hourOffsetPct =
    totalBlocks > 0 ? ((firstHourBlock - rangeStart) / totalBlocks) * 100 : 0;

  // Viewport overlay box — clip the visible block range to the timeline's
  // own range and convert to %. If the visible range falls completely
  // outside the timeline's range (e.g. operator deselected the shifts that
  // covered the visible window) we hide the box.
  let viewport:
    | { leftPct: number; widthPct: number }
    | null = null;
  if (
    typeof visibleStartBlock === 'number' &&
    typeof visibleCloseBlock === 'number' &&
    totalBlocks > 0 &&
    visibleCloseBlock > visibleStartBlock
  ) {
    const clippedStart = Math.max(rangeStart, visibleStartBlock);
    const clippedClose = Math.min(rangeClose, visibleCloseBlock);
    if (clippedClose > clippedStart) {
      viewport = {
        leftPct: ((clippedStart - rangeStart) / totalBlocks) * 100,
        widthPct: ((clippedClose - clippedStart) / totalBlocks) * 100,
      };
    }
  }

  return (
    <div
      className="shiftTimeline"
      onDragOver={(e) => e.preventDefault()}
      role="group"
      aria-label="Shifts"
    >
      <div
        className="shiftTimeline-track"
        style={
          {
            height: trackHeight,
            '--shiftTimeline-hour-width': `${hourWidthPct}%`,
            '--shiftTimeline-hour-offset': `${hourOffsetPct}%`,
          } as React.CSSProperties
        }
      >
        {laidOut.map(({ shift, row }) => {
          const selected = selectedShiftIds.includes(shift.id as string);
          const leftPct =
            totalBlocks > 0
              ? ((shift.start - rangeStart) / totalBlocks) * 100
              : 0;
          const widthPct =
            totalBlocks > 0
              ? ((shift.close - shift.start) / totalBlocks) * 100
              : 0;
          const top = row * (ROW_HEIGHT + ROW_GAP);
          const startLabel = blockToTime(shift.start);
          const closeLabel = blockToTime(shift.close);

          return (
            <button
              key={shift.id}
              type="button"
              className={classHelper([
                'shiftCard',
                selected && 'selected',
                !selected && 'deselected',
                shift.closed && 'shiftClosed',
              ])}
              style={
                {
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                  top,
                  height: ROW_HEIGHT,
                  // Each shift drives its own color. When the shift has no
                  // explicit color set we fall back to the primary palette
                  // so the timeline stays visually consistent with the rest
                  // of the app.
                  '--shift-color': shift.color || 'var(--color-primary)',
                } as React.CSSProperties
              }
              onClick={() => shift.id && onToggle(shift.id as string)}
              aria-pressed={selected}
              title={`${shift.name || 'Shift'} · ${startLabel}–${closeLabel}`}
            >
              <span className="shiftCard-check" aria-hidden="true">
                {selected ? (
                  <CheckBox fontSize="small" />
                ) : (
                  <CheckBoxOutlineBlank fontSize="small" />
                )}
              </span>
              <span className="shiftCard-text">
                <span className="shiftCard-name">{shift.name || 'Shift'}</span>
                <span className="shiftCard-time">
                  {startLabel}–{closeLabel}
                </span>
              </span>
            </button>
          );
        })}
        {viewport && (
          <div
            className="shiftTimeline-viewport"
            style={{
              left: `${viewport.leftPct}%`,
              width: `${viewport.widthPct}%`,
            }}
            aria-hidden="true"
          />
        )}
      </div>
      {(onZoomIn || onZoomOut) && (
        <div className="shiftTimeline-zoom">
          <button
            type="button"
            className="shiftTimeline-zoomBtn"
            onClick={onZoomOut}
            disabled={!canZoomOut}
            aria-label="Zoom out"
            title="Zoom out"
          >
            <ZoomOut fontSize="small" />
          </button>
          <button
            type="button"
            className="shiftTimeline-zoomBtn"
            onClick={onZoomIn}
            disabled={!canZoomIn}
            aria-label="Zoom in"
            title="Zoom in"
          >
            <ZoomIn fontSize="small" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ShiftTimeline;
